import React, { useEffect, useRef, useState } from "react";
import ASSET_PATHS from "../../../assets/config/assetPaths";
import { ImSpinner2 } from "react-icons/im";
import { WorkOrderStatus } from "../../../types/workOrder";
import { FaTimes } from "react-icons/fa";

interface PreviewImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  uploadedImage: string[] | [];
  openGallery: (
    event: React.ChangeEvent<HTMLInputElement> | Event,
    type: string
  ) => Promise<void>;
  workOrderDetail: any;
  workOrderId: string | undefined;
  setCurrentCapturedDetail: (object: any) => void;
  handleRemoveImage: (index: number) => void;
  onAddMoreClick: () => void;
}

const PreviewImageUploadModal: React.FC<PreviewImageUploadModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  uploadedImage,
  openGallery,
  workOrderDetail,
  workOrderId,
  handleRemoveImage,
  onAddMoreClick,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS device using modern approach
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error("Error confirming action:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMoreClick = () => {
    if (isIOS) {
      // For iOS, just trigger the file input click
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } else {
      // For non-iOS, call the parent component's handler
      onAddMoreClick();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div
        ref={modalRef}
        className="w-[92%] max-w-md sm:max-w-lg md:max-w-xl bg-white rounded-lg shadow-xl p-3 sm:p-5 md:p-6 max-[350px]:p-2 max-[300px]:p-1 relative mx-4 max-[360px]:w-[96%] max-[350px]:mx-2 max-[300px]:mx-1"      >
        <button
          className="absolute top-1 right-1 text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          <img src={ASSET_PATHS.closeIcon} alt="Close Icon" />
        </button>
        <div
          className="w-full h-64 flex items-center border border-gray-300 rounded-md mb-4"
          style={{ overflowX: "auto", gap: 10 }}
        >
          {uploadedImage && uploadedImage.length > 0 ? (
            uploadedImage.map((image, index) => (
              <div
                key={index}
                className="flex-shrink-0 mr-4"
                style={{ position: "relative", height: "200px", width: "200px" }}
              >
                <img
                  src={image}
                  alt="Captured Preview"
                  className="object-contain w-full h-full rounded-md"
                />
                <button
                  onClick={() => handleRemoveImage(index)}
                  style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    color: "white",
                    borderRadius: "50%",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTimes size={12} />
                </button>
              </div>
            ))
          ) : (
            <p className="text-gray-500">Photo preview</p>
          )}
        </div>

        <div className="flex flex-row gap-4 max-[640px]:gap-3 max-[480px]:gap-2 max-[380px]:gap-1 max-[280px]:flex-col">
          <div className="flex-1">
            <label htmlFor="another-file-upload">
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 
                           border-2 border-blue-600 text-blue-600 py-2 rounded-md
                           text-sm sm:text-base max-[350px]:gap-1 min-[280px]:text-xs max-[300px]:text-xs"
                onClick={handleAddMoreClick}
              >
                <img
                  src={ASSET_PATHS.blueCameraIcon}
                  alt="Camera"
                  className="h-5 w-5"
                />
                <span className="whitespace-nowrap">Add Media</span>
              </button>
            </label>
            <input
              ref={fileInputRef}
              id={`another-file-upload`}
              type="file"
              accept="image/*"
              multiple={true}
              className="hidden"
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                e.currentTarget.value = "";
              }}
              onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                await openGallery(event, "image");
                event.target.value = "";
              }}
              disabled={
                workOrderDetail[+workOrderId!].workOrderStatus === WorkOrderStatus.Completed
              }
            />
          </div>

          <button
            className="flex-1 w-full bg-blue-600 text-white py-2 rounded-md 
                       flex items-center justify-center gap-2 disabled:opacity-60
                       text-sm sm:text-base max-[350px]:gap-1 min-[280px]:text-xs max-[300px]:text-xs"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <ImSpinner2 className="animate-spin" />
            ) : (
              <>
                <img
                  src={ASSET_PATHS.cloudUploadWhite}
                  alt="Upload"
                  className="h-5 w-5"
                />
                <span className="whitespace-nowrap">Confirm Upload</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewImageUploadModal;
